/**
 * The data layer for the workspace-wide activations list.
 *
 * The list is a two-hop against existing platform routes — `listSqlAudiences` for the sources, then
 * `listSyncsForSource` per source — because RudderStack has no "every sync in the workspace" route
 * and this vertical ships no new platform API. That makes it an N+1, which is fine at private-beta
 * scale but is the reason this lives in one hook rather than being open-coded in a view: when the
 * fan-out moves server-side behind a bounded-concurrency aggregate, exactly one file changes.
 *
 * `listDestinations` is the third call and is NOT part of the fan-out — one flat lookup resolves
 * every row's destination, because a sync names its destination only by id and the row needs its
 * platform (and, where a human named it, its name).
 *
 * `listActivationMeta` is the fourth, and is flat for a different reason: it reads Lookout's OWN
 * table, so it is one indexed query for the whole workspace no matter how many audiences there are.
 * It is deliberately outside `isLoading` and outside `error` as well — see the two comments at the
 * bottom of the hook. A missing display name must never hold up, or discredit, a list of live syncs.
 */
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useOrpc } from '@/_shared/hooks/useOrpc';

import type { ActivationRow, DestinationRef, RawActivationMeta, RawSqlAudience, RawSync } from '../types';
import { isDeliveryEnabled } from './activation-list';

export interface ActivationsData {
  rows: ActivationRow[];
  /** sourceId → the audience's name in RudderStack. Rows carry ids; names resolve here. */
  audienceNames: Map<string, string>;
  audiences: RawSqlAudience[];
  isLoading: boolean;
  /** The first failure across all three hops — a partial list is still an error worth surfacing. */
  error: Error | null;
  refetch: () => void;
}

export function useActivations(): ActivationsData {
  const orpc = useOrpc();
  const queryClient = useQueryClient();

  const audiencesQuery = useQuery(orpc.activations.listSqlAudiences.queryOptions({ input: {} }));
  const destinationsQuery = useQuery(orpc.activations.listDestinations.queryOptions({ input: {} }));
  const metaQuery = useQuery(orpc.activations.listActivationMeta.queryOptions({ input: {} }));

  const audiences = useMemo<RawSqlAudience[]>(() => audiencesQuery.data?.sources ?? [], [audiencesQuery.data?.sources]);

  // `combine` rather than reading the raw results array: it collapses the fan-out into one
  // structurally-shared value, so a refetch that returns a byte-identical payload yields the SAME
  // `syncs` reference. Keying downstream memos on a timestamp (`dataUpdatedAt`) instead would bump on
  // every successful fetch even when nothing changed — rebuilding every row and snapping the table's
  // scroll position to the top (`Table` keys `resetScrollOnDataChange` on `data` identity).
  const syncFanOut = useQueries({
    queries: audiences.map((audience) =>
      orpc.activations.listSyncsForSource.queryOptions({ input: { sourceId: audience.id } }),
    ),
    combine: (results) => ({
      syncs: results.flatMap((result) => (result.data?.syncs ?? []) as RawSync[]),
      isLoading: results.some((result) => result.isLoading),
      error: results.find((result) => result.error)?.error ?? null,
    }),
  });

  const destinationsById = useMemo(() => {
    const map = new Map<string, DestinationRef>();
    for (const destination of destinationsQuery.data?.destinations ?? []) map.set(destination.id, destination);
    return map;
  }, [destinationsQuery.data?.destinations]);

  // Keyed by `connectionId`, which IS the sync id — the far side of the join `listSyncsForSource`
  // produces. Most syncs have no entry (webapp-built ones never do), so this is a lookup that expects
  // to miss, not a map that must be total.
  const metaByConnection = useMemo(() => {
    const map = new Map<string, RawActivationMeta>();
    for (const meta of metaQuery.data?.activations ?? []) map.set(meta.connectionId, meta);
    return map;
  }, [metaQuery.data?.activations]);

  // The source half of the delivery switch. `listSqlAudiences` has always projected `enabled`; until
  // the three-bit rule landed nothing read it.
  const sourceEnabledById = useMemo(() => {
    const map = new Map<string, boolean>();
    for (const audience of audiences) map.set(audience.id, audience.enabled ?? true);
    return map;
  }, [audiences]);

  const warehouseTypeById = useMemo(() => {
    const map = new Map<string, string | undefined>();
    for (const audience of audiences) map.set(audience.id, audience.sourceDefinitionName);
    return map;
  }, [audiences]);

  const audienceNames = useMemo(() => {
    const map = new Map<string, string>();
    for (const audience of audiences) map.set(audience.id, audience.name);
    return map;
  }, [audiences]);

  // A sync already carries its own `sourceId`, so the flattened fan-out needs no zipping back to the
  // audience that produced it.
  const rows = useMemo<ActivationRow[]>(
    () =>
      syncFanOut.syncs.map((sync) => {
        const meta = metaByConnection.get(sync.id);
        const destination = destinationsById.get(sync.destinationId) ?? null;
        // A destination that has not resolved yet is not evidence of a disabled one.
        const destinationEnabled = destination?.enabled ?? true;
        return {
          syncId: sync.id,
          audienceId: sync.sourceId,
          warehouseType: warehouseTypeById.get(sync.sourceId),
          destination,
          // The sync's own config first: it is on the wire for EVERY sync, recorded or not, so the id
          // rung of the ladder holds even for the webapp-built syncs `listActivationMeta` never sees.
          destinationAudienceId: sync.config?.destination?.audienceId ?? meta?.destinationAudienceId,
          destinationAudienceName: meta?.destinationAudienceName ?? null,
          // Three switches, ANDed — see `isDeliveryEnabled`, which the detail page shares.
          enabled: isDeliveryEnabled({
            sync: sync.enabled,
            source: sourceEnabledById.get(sync.sourceId),
            destination: destinationEnabled,
          }),
          schedule: sync.config?.source?.schedule,
          lastRun: sync.lastRun,
          updatedAt: sync.updatedAt,
        };
      }),
    [syncFanOut.syncs, destinationsById, metaByConnection, sourceEnabledById, warehouseTypeById],
  );

  return {
    rows,
    audienceNames,
    audiences,
    // `metaQuery` is deliberately absent from BOTH of the next two lines.
    //
    // Not in `isLoading`: rows render from ids the moment the syncs land, and the name upgrades in
    // place. Gating the skeleton on a display name would make the whole list wait on the one datum it
    // can do without.
    //
    // Not in `error`: the page turns this into "this list may be incomplete", which would be a lie. A
    // failed meta read hides no row and mis-states no run — every row still renders, titled by its
    // far-side id. Under-reporting is this surface's worst failure; over-alarming is a close second.
    isLoading: audiencesQuery.isLoading || destinationsQuery.isLoading || syncFanOut.isLoading,
    error: audiencesQuery.error ?? destinationsQuery.error ?? syncFanOut.error,
    refetch: () => {
      void audiencesQuery.refetch();
      void destinationsQuery.refetch();
      void metaQuery.refetch();
      // The fan-out has no stable handle to refetch through once `combine` has collapsed it —
      // invalidating the namespace refetches every source's syncs, which is exactly the intent.
      void queryClient.invalidateQueries({ queryKey: orpc.activations.listSyncsForSource.key() });
    },
  };
}
